import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search, MessageCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { navLinks } from "@/config/site";
import { business } from "@/config/business";
import { buildWhatsAppUrl, generalMessage } from "@/lib/whatsapp";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSearchOpen(false); }, [pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/phones?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "border-b border-border bg-[hsl(var(--paper))]/85 backdrop-blur-xl" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <div className="container-wide flex h-16 items-center justify-between gap-4">
        <Link to="/" className="font-display text-lg font-extrabold uppercase tracking-tight" data-testid="nav-logo">
          Mobile <span className="accent-text">Specialist</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {navLinks.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-full px-3 py-2 text-sm font-medium text-[hsl(var(--ink-soft))] transition-colors hover:bg-white hover:text-[hsl(var(--ink))]"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button onClick={() => setSearchOpen((v) => !v)} aria-label="Search phones" data-testid="nav-search-btn" className="rounded-full p-2.5 transition-colors hover:bg-white">
            <Search className="h-[18px] w-[18px]" />
          </button>
          <a href={buildWhatsAppUrl(generalMessage())} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp" aria-label="WhatsApp us" className="hidden rounded-full bg-[hsl(var(--accent))] p-2.5 text-white transition-transform hover:scale-105 sm:inline-flex">
            <MessageCircle className="h-[18px] w-[18px]" />
          </a>
          <button onClick={() => setOpen((v) => !v)} aria-label="Open menu" data-testid="nav-menu-btn" className="rounded-full p-2.5 transition-colors hover:bg-white lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.form
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            onSubmit={submitSearch} className="overflow-hidden border-t border-border bg-[hsl(var(--paper))]"
          >
            <div className="container-wide flex items-center gap-3 py-3">
              <Search className="h-4 w-4 text-[hsl(var(--ink-soft))]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search phones (e.g. iPhone, Samsung)…" data-testid="nav-search-input" className="w-full bg-transparent text-sm outline-none placeholder:text-[hsl(var(--ink-soft))]" />
              <button type="submit" className="btn-ink px-4 py-2 text-xs">Search</button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-border bg-[hsl(var(--paper))] lg:hidden" aria-label="Mobile"
          >
            <div className="container-wide flex flex-col py-3">
              {navLinks.map((l) => (
                <Link key={l.label} to={l.to} className="border-b border-border/60 py-3 text-base font-medium last:border-0" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <a href={`tel:${business.phone}`} className="btn-ink mt-4">Call {business.phoneDisplay}</a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
